import { useEffect, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { getProjectSites } from '../api/projects';
import { getFloorsBySiteId } from '../api/locations';

export interface LocationsData {
  rooms: any[];
  sites: any[];
  loading: boolean;
  error: string | null;
}

// Module-level cache to prevent duplicate requests (e.g., React StrictMode)
const locationsCache = new Map<string, LocationsData>();
const locationsLoading = new Set<string>();

export const useProjectLocationsData = (projectId: string, archived: boolean = false) => {
  const { keycloak: { token } } = useKeycloak();
  const [data, setData] = useState<LocationsData>({
    rooms: [],
    sites: [],
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (!token || !projectId || archived) {
      setData({ rooms: [], sites: [], loading: false, error: null });
      return;
    }

    const cacheKey = `${projectId}:${archived}`;

    // Check if already cached
    if (locationsCache.has(cacheKey)) {
      setData(locationsCache.get(cacheKey)!);
      return;
    }

    // Skip if already loading (StrictMode will run effect again after promise resolves)
    if (locationsLoading.has(cacheKey)) {
      return;
    }

    const loadLocationsData = async () => {
      locationsLoading.add(cacheKey);
      setData(prev => ({ ...prev, loading: true, error: null }));
      try {
        const sitesData = await getProjectSites(token, projectId);

        // Fetch floors for each site (floors response includes rooms)
        const floorsPerSite = await Promise.all(
          (Array.isArray(sitesData) ? sitesData : []).map(site =>
            getFloorsBySiteId(token, projectId, site.id, { archived })
          )
        );

        // Extract all rooms from floors (backend includes rooms in floor response)
        const allRooms: any[] = [];
        floorsPerSite.forEach(floors => {
          if (Array.isArray(floors)) {
            floors.forEach(floor => {
              if (Array.isArray(floor.rooms)) {
                allRooms.push(...floor.rooms);
              }
            });
          }
        });

        const result: LocationsData = {
          rooms: allRooms,
          sites: Array.isArray(sitesData) ? sitesData : [],
          loading: false,
          error: null,
        };

        locationsCache.set(cacheKey, result);
        locationsLoading.delete(cacheKey);
        setData(result);
      } catch (error) {
        console.error('Failed to load locations data:', error);
        const errorResult: LocationsData = {
          rooms: [],
          sites: [],
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to load locations data',
        };
        locationsLoading.delete(cacheKey);
        setData(errorResult);
      }
    };

    loadLocationsData();
  }, [token, projectId, archived]);

  return data;
};
